import { Session } from '@supabase/supabase-js';
import { z } from 'zod';
import { supabase } from '@/integrations/supabase/client';
import { supabaseRpcWithRetry, supabaseQueryWithRetry } from '@/lib/networkRetry';
import { createLogger } from '@/lib/structuredLogger';
import { OwnedCoupon, Reward, Redemption, RewardsData, RewardStockMap } from '@/types/rewards';

const logger = createLogger('RewardsData');

const rewardSummarySchema = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string(),
  points_cost: z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER),
  category: z.string(),
});

const rewardsDataSchema = z.object({
  rewards: z.array(
    rewardSummarySchema.extend({
      active: z.boolean(),
      created_at: z.string(),
    }),
  ),
  user_points: z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER),
  redemptions: z.array(
    z.object({
      id: z.string(),
      reward_id: z.string(),
      qr_code: z.string(),
      created_at: z.string(),
      rewards: rewardSummarySchema,
    }),
  ),
});

const rewardStockSchema = z
  .record(
    z.string().regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/).max(80),
    z.number().int().nonnegative().max(1_000_000),
  )
  .superRefine((stock, context) => {
    if (Object.keys(stock).length > 100) {
      context.addIssue({
        code: z.ZodIssueCode.too_big,
        maximum: 100,
        type: 'array',
        inclusive: true,
        message: 'Reward stock contains too many partners',
      });
    }
  });

// Re-export types for backward compatibility
export type { Reward, Redemption, RewardsData };
export type { OwnedCoupon, RewardStockMap };

export async function fetchRewardsData(userId: string, _session: Session): Promise<RewardsData> {
  try {
    const { data, error } = await supabaseRpcWithRetry<unknown>(supabase, 'get_rewards_data', {
      user_id_param: userId,
    });
    if (error) throw new Error('Rewards batch unavailable');
    const parsed = rewardsDataSchema.safeParse(data);
    if (!parsed.success) throw new Error('Invalid rewards batch');
    return parsed.data;
  } catch {
    logger.warn('Batched rewards unavailable; using fallback reads');
  }

  return fetchRewardsDataFallback(userId);
}

export async function fetchRewardStock(): Promise<RewardStockMap> {
  const { data, error } = await supabaseRpcWithRetry<unknown>(
    supabase,
    'get_member_reward_stock',
  );

  if (error) {
    logger.error(
      'Reward stock fetch failed',
      error instanceof Error ? error : new Error(String(error)),
    );
    throw error;
  }

  const parsed = rewardStockSchema.safeParse(data);
  if (!parsed.success) {
    logger.error('Reward stock response validation failed', new Error('Invalid reward stock'));
    throw new Error('Invalid reward stock response');
  }

  return parsed.data;
}

export async function fetchOwnedCoupons(userId: string): Promise<OwnedCoupon[]> {
  const { data, error } = await supabaseQueryWithRetry(() =>
    supabase
      .from('coupons')
      .select('id, partner, code, assigned_at')
      .eq('user_id', userId)
      .order('assigned_at', { ascending: false }),
  );

  if (error) {
    logger.error(
      'Owned coupons fetch failed',
      error instanceof Error ? error : new Error(String(error)),
      { userId },
    );
    throw error;
  }

  return (data ?? []) as OwnedCoupon[];
}

async function fetchRewardsDataFallback(userId: string): Promise<RewardsData> {
  // The wrapper owns retries; disable the SDK's additional GET retry loop.
  const [rewards, balance, redemptions] = await Promise.all([
    supabaseQueryWithRetry(() =>
      supabase
        .from('rewards')
        .select('*')
        .eq('active', true)
        .order('points_cost', { ascending: true })
        .retry(false),
    ),
    // Use the ledger authority even when an older profile cache is nonnull.
    supabaseRpcWithRetry<unknown>(
      supabase,
      'calculate_points_total',
      { p_user_id: userId },
    ),
    supabaseQueryWithRetry(() =>
      supabase
        .from('redemptions')
        .select('*, rewards(*)')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .retry(false),
    ),
  ]);

  if (rewards.error || balance.error || redemptions.error) {
    throw new Error('Unable to load rewards');
  }

  const parsed = rewardsDataSchema.safeParse({
    rewards: rewards.data,
    user_points: balance.data,
    redemptions: redemptions.data,
  });
  if (!parsed.success) throw new Error('Invalid rewards response');
  return parsed.data;
}
